import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus } from 'lucide-react';
import { stockAdjustmentApi } from '../api/stockAdjustment';
import { purchaseApi } from '../api/purchase';
import { useToast } from '../hooks/useToast';
import * as XLSX from 'xlsx';
import api from '../api/client';

const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', red:'badge-red' };

const StockAdjustment: React.FC = () => {
  const { showToast } = useToast();
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [stockItems, setStockItems] = useState<any[]>([]);
  const [form, setForm] = useState({ branch_id:'', location_id:'', adjustment_date: new Date().toISOString().split('T')[0], reason:'' });
  const [lines, setLines] = useState<any[]>([]);
  const [uploadPreview, setUploadPreview] = useState<any[]>([]);
  const [uploadDate, setUploadDate] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    stockAdjustmentApi.list({ search }).then(res => { setAdjustments(res.data.data); setTotal(res.data.total); setLoading(false); });
  }, [search]);

  useEffect(() => {
    purchaseApi.branches().then(r => setBranches(r.data.data));
    purchaseApi.locations().then(r => setLocations(r.data.data));
    load();
  }, [load]);

  const loadStock = (branchId: string, locationId: string) => {
    if (!branchId) return;
    api.get('/inventory', { params: { branch_id: branchId, location_id: locationId||undefined, limit:500 } })
      .then(r => {
        setStockItems(r.data.data || []);
        setLines((r.data.data || []).map((item: any) => ({
          product_id:   item.product_id,
          location_id:  item.location_id,
          product_name: item.product_name,
          sku:          item.sku,
          location_name:item.location_name,
          branch_name:  item.branch_name,
          system_qty:   Number(item.qty_on_hand),
          physical_qty: Number(item.qty_on_hand),
          unit_cost:    Number(item.unit_cost) || Number(item.cost_price),
          difference:   0,
        })));
      });
  };

  const handleSubmit = async () => {
    if (!form.branch_id) { showToast('Branch required', 'error'); return; }
    setSaving(true);
    try {
      const res = await stockAdjustmentApi.create({
        branch_id: Number(form.branch_id),
        adjustment_date: form.adjustment_date,
        reason: form.reason,
        items: lines.map(l => ({ product_id:l.product_id, location_id:l.location_id, system_qty:l.system_qty, physical_qty:l.physical_qty, unit_cost:l.unit_cost })),
      });
      showToast(res.data.message);
      setShowForm(false); setLines([]); setStockItems([]);
      setForm({ branch_id:'', location_id:'', adjustment_date: new Date().toISOString().split('T')[0], reason:'' });
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(ws);
      const preview: any[] = [];
      let adjDate = new Date().toISOString().split('T')[0];
      for (const row of rows) {
        const counted = row['Counted Qty'];
        if (counted === undefined || counted === '') continue;
        const rawDate = row['As of Date (YYYY-MM-DD)'];
        if (rawDate) {
          if (typeof rawDate === 'number') {
            const d = XLSX.SSF.parse_date_code(rawDate);
            adjDate = d.y + '-' + String(d.m).padStart(2,'0') + '-' + String(d.d).padStart(2,'0');
          } else {
            const s = rawDate.toString();
            adjDate = s.includes('-') ? s : new Date(s).toISOString().split('T')[0];
          }
        }
        preview.push({
          sku: row['SKU'],
          product: row['Product'],
          branch: row['Branch'],
          location: row['Location'],
          system_qty: Number(row['System Qty']),
          unit_cost: Number(row['Unit Cost'] || 0),
          counted_qty: Number(counted),
          difference: Number(counted) - Number(row['System Qty']),
          coa_code: row['COA Account Code'] || '5100',
        });
      }
      setUploadPreview(preview);
      setUploadDate(adjDate);
      setShowUpload(true);
    } catch (err: any) { showToast('Failed to parse file', 'error'); }
    finally { setUploading(false); if (e.target) e.target.value = ''; }
  };

  const handlePostUpload = async () => {
    // Validate duplicates
    const seen = new Set<string>();
    for (const row of uploadPreview) {
      const key = row.sku+"|"+row.product+"|"+row.branch+"|"+row.location;
      if (seen.has(key)) { showToast("Duplicate: "+row.product+" ("+row.sku+") at "+row.branch+" / "+row.location, "error"); setSaving(false); return; }
      seen.add(key);
    }
    setSaving(true);
    try {
      // Prefetch locations + inventory once per unique branch (avoid N+1 requests)
      const uniqueBranches = Array.from(new Set(uploadPreview.map((r: any) => r.branch)));
      const locByBranch: Record<string, any[]> = {};
      const invByLocation: Record<number, any[]> = {};
      for (const branchName of uniqueBranches) {
        const branchRec = branches.find((b: any) => b.name === branchName);
        if (!branchRec) continue;
        const locRes = await api.get("/locations", { params: { branch_id: branchRec.id } });
        locByBranch[branchName] = locRes.data.data;
        for (const loc of locRes.data.data) {
          const invRes = await api.get("/inventory", { params: { location_id: loc.id, limit: 10000, include_zero: 1 } });
          invByLocation[loc.id] = invRes.data.data;
        }
      }

      let failCount = 0;
      for (const row of uploadPreview) {
        const branchRec = branches.find((b: any) => b.name === row.branch);
        if (!branchRec) { showToast("Branch not found: "+row.branch, "error"); failCount++; continue; }
        const locs = locByBranch[row.branch] || [];
        const loc = locs.find((l: any) => l.name === row.location);
        const inv = loc ? (invByLocation[loc.id] || []).find((i: any) => i.sku === row.sku) : null;
        if (!loc || !inv) { showToast("Not found: "+row.product+" at "+row.location, "error"); failCount++; continue; }
        if (inv.product_name && row.product && inv.product_name.toLowerCase() !== row.product.toLowerCase()) { showToast("Name mismatch: "+row.sku+" - file: "+row.product+" system: "+inv.product_name, "error"); failCount++; continue; }
        const unitCost = row.unit_cost > 0 ? row.unit_cost : Number(inv.cost_price);
        await api.post("/stock-adjustments", { branch_id: branchRec.id, adjustment_date: uploadDate, reason: "Physical Count - "+row.product+" ("+row.sku+")", items: [{ product_id: inv.product_id, location_id: loc.id, system_qty: row.system_qty, physical_qty: row.counted_qty, unit_cost: unitCost }] });
      }
      showToast(failCount > 0 ? ("Posted with "+failCount+" errors — check toasts above") : 'Stock adjustment posted successfully');
      setShowUpload(false); setUploadPreview([]); load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Stock Adjustment</h1>
        <div style={{ display:'flex', gap:8 }}>
          <label className="btn btn-secondary" style={{ cursor:'pointer' }}>
            {uploading ? 'Reading...' : 'Upload Physical Count'}
            <input type="file" accept=".xlsx,.xls" style={{ display:'none' }} onChange={handleUploadFile} />
          </label>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13}/> New Adjustment</button>
        </div>
      </div>

      {showUpload && uploadPreview.length > 0 && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header">
            <span>Upload Preview - {uploadDate} - {uploadPreview.length} items</span>
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowUpload(false); setUploadPreview([]); }}>Cancel</button>
          </div>
          <div className="card-body" style={{ padding:0 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>SKU</th>
                <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Branch</th>
                <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
                <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>System Qty</th>
                <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Counted Qty</th>
                <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Difference</th>
                <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Unit Cost</th>
              </tr></thead>
              <tbody>
                {uploadPreview.map((r, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                    <td style={{ padding:'8px 12px', fontSize:11, fontFamily:'monospace', color:'var(--blue)' }}>{r.sku}</td>
                    <td style={{ padding:'8px 12px', fontSize:11 }}>{r.product}</td>
                    <td style={{ padding:'8px 12px', fontSize:11, color:'var(--tx3)' }}>{r.branch}</td>
                    <td style={{ padding:'8px 12px', fontSize:11, color:'var(--tx3)' }}>{r.location}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, textAlign:'right' }}>{r.system_qty.toLocaleString()}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, textAlign:'right', fontWeight:600 }}>{r.counted_qty.toLocaleString()}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, textAlign:'right', fontWeight:600, color: r.difference > 0 ? 'var(--green)' : r.difference < 0 ? 'var(--red)' : 'var(--tx3)' }}>{r.difference > 0 ? '+' : ''}{r.difference}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, textAlign:'right' }}>{r.unit_cost.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding:'12px 16px', display:'flex', justifyContent:'flex-end', gap:8 }}>
              <button className="btn btn-secondary" onClick={() => { setShowUpload(false); setUploadPreview([]); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handlePostUpload} disabled={saving}>{saving ? 'Posting...' : 'Post Stock Adjustment'}</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span>Physical Count Adjustment</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Branch *</label>
                <select className="form-control" value={form.branch_id} onChange={e => { setForm(f=>({...f,branch_id:e.target.value})); loadStock(e.target.value, form.location_id); }}>
                  <option value="">Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Location (optional)</label>
                <select className="form-control" value={form.location_id} onChange={e => { setForm(f=>({...f,location_id:e.target.value})); loadStock(form.branch_id, e.target.value); }}>
                  <option value="">All Locations</option>
                  {locations.filter(l => String(l.branch_id) === String(form.branch_id)).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Adjustment Date *</label>
                <input type="date" className="form-control" value={form.adjustment_date} onChange={e => setForm(f=>({...f,adjustment_date:e.target.value}))} />
              </div>
              <div className="form-group"><label className="form-label">Reason</label>
                <input className="form-control" value={form.reason} onChange={e => setForm(f=>({...f,reason:e.target.value}))} placeholder="Physical count, correction..." />
              </div>
            </div>
            {lines.length > 0 && (
              <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:12 }}>
                <thead><tr style={{ background:'var(--sur2)' }}>
                  <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>SKU</th>
                  <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                  <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
                  <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>System Qty</th>
                  <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Counted Qty</th>
                  <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Unit Cost</th>
                  <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Difference</th>
                </tr></thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                      <td style={{ padding:'6px 10px', fontSize:11, fontFamily:'monospace', color:'var(--blue)' }}>{l.sku}</td>
                      <td style={{ padding:'6px 10px', fontSize:11 }}>{l.product_name}</td>
                      <td style={{ padding:'6px 10px', fontSize:11, color:'var(--tx3)' }}>{l.location_name}</td>
                      <td style={{ padding:'6px 10px', fontSize:12, textAlign:'right' }}>{l.system_qty}</td>
                      <td style={{ padding:'4px 6px', textAlign:'right' }}>
                        <input type="number" className="form-control" style={{ width:80, textAlign:'right', fontSize:12 }}
                          value={l.physical_qty}
                          onChange={e => setLines(ls => ls.map((x,j) => j===i ? {...x, physical_qty:Number(e.target.value), difference:Number(e.target.value)-x.system_qty} : x))} />
                      </td>
                      <td style={{ padding:'6px 10px', fontSize:12, textAlign:'right' }}>{l.unit_cost.toFixed(2)}</td>
                      <td style={{ padding:'6px 10px', fontSize:12, textAlign:'right', fontWeight:600, color: l.difference > 0 ? 'var(--green)' : l.difference < 0 ? 'var(--red)' : 'var(--tx3)' }}>
                        {l.difference > 0 ? '+' : ''}{l.difference}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || !lines.length}>{saving ? 'Posting...' : 'Post Adjustment'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)" /><input placeholder="Search adjustments..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <span className="record-count">{total} records</span>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr>
            <th>Reference</th><th>Branch</th><th>Date</th>
            <th style={{textAlign:'right'}}>Increase</th>
            <th style={{textAlign:'right'}}>Decrease</th>
            <th>Status</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6}><div className="empty-state"><p>Loading...</p></div></td></tr> :
             adjustments.length === 0 ? <tr><td colSpan={6}><div className="empty-state"><p>No adjustments yet</p></div></td></tr> :
             adjustments.map((a: any) => (
              <tr key={a.id}>
                <td className="td-code">{a.reference_no}</td>
                <td>{a.branch_name}</td>
                <td style={{fontSize:11,color:'var(--tx3)'}}>{a.adjustment_date}</td>
                <td className="td-num td-green">{Number(a.total_increase).toFixed(2)}</td>
                <td className="td-num td-red">{Number(a.total_decrease).toFixed(2)}</td>
                <td><span className={`badge ${colorMap[a.status_color]||'badge-gray'}`}>{a.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default StockAdjustment;